import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useVideoGeneration } from '../hooks/useVideoGeneration';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import { useAssetManagement } from '../hooks/useAssetManagement';
import { useProjectContext } from '../hooks/useProjectContext';
import { toast as toastBus } from '../utils/toastBus';
import { ProjectBasics } from '../components/ProjectBasics';
import { PromptSection } from '../components/PromptSection';
import { AssetCreationFlow } from '../components/AssetCreationFlow';
import { QuickActions, WorkflowWizard } from '../components/QuickActions';
import { UnifiedProgress } from '../components/UnifiedProgress';
import { ProjectSelector } from '../components/ProjectSelector';
import AssetGallery from '../components/AssetGallery';
import VideoGallery from '../components/VideoGallery';
import StaticVideoModal from '../components/StaticVideoModal';
import { VideoGenerationErrorBoundary } from '../components/VideoGenerationErrorBoundary';
import { sanitizePromptText, cleanPrompts } from '../utils/promptUtils';
import { TIER_OPTIONS, TEMPLATE_OPTIONS } from '../utils/constants';
import { projectService } from '../services/projectService';
import api, { resolveMediaUrl } from '../services/api';

const VIDEO_PAGE_SIZE = 50;

function LatestAssetChip({ label, hint, onClick, thumbUrl }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        background: '#111827',
        border: '1px solid #1f2937',
        borderRadius: 10,
        cursor: 'pointer',
        minWidth: 0
      }}
    >
      {thumbUrl ? (
        <img
          src={thumbUrl}
          alt={label}
          style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', border: '1px solid #1f2937' }}
          loading="lazy"
        />
      ) : (
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            background: '#1f2937',
            display: 'grid',
            placeItems: 'center',
            color: '#9ca3af',
            fontSize: 12,
            border: '1px solid #1f2937'
          }}
        >
          {label.slice(0, 1)}
        </div>
      )}
      <div style={{ textAlign: 'left', minWidth: 0 }}>
        <div style={{ color: '#f9fafb', fontWeight: 600, fontSize: 13, lineHeight: 1.2 }}>{label}</div>
        {hint ? <div style={{ color: '#9ca3af', fontSize: 12, lineHeight: 1.2 }}>{hint}</div> : null}
      </div>
    </button>
  );
}

export default function ProjectDetails() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const videoHandledRef = React.useRef({ completed: false, failed: false });
  
  // Asset generation states
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [staticModalOpen, setStaticModalOpen] = useState(false);
  
  // Progress tracking
  const [activeGenerations, setActiveGenerations] = useState([]);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showPrompts, setShowPrompts] = useState(false);
  
  // Prompt states
  const [audioPrompts, setAudioPrompts] = useState([]);
  const [imagePrompts, setImagePrompts] = useState([]);
  const [videoPrompts, setVideoPrompts] = useState([]);
  const [audioSpecificContext, setAudioSpecificContext] = useState('');
  const [audioSelectedPrompt, setAudioSelectedPrompt] = useState('');
  const [imageSpecificContext, setImageSpecificContext] = useState('');
  const [imageSelectedPrompt, setImageSelectedPrompt] = useState('');
  const [videoSpecificContext, setVideoSpecificContext] = useState('');
  const [videoSelectedPrompt, setVideoSelectedPrompt] = useState('');
  const [selectedTypes, setSelectedTypes] = useState({
    audio: false,
    image: false,
    video: false
  });
  
  // Hooks
  const { audioPlayerRef, activeAudio, audioPlayerStatus, playAudio } = useAudioPlayer();
  const { assets, archived, loadAssets, archiveAsset, restoreAsset, deleteArchivedAsset, pagination } = useAssetManagement(id);
  const { context, updateContext, saveContext } = useProjectContext(id);
  const videoGen = useVideoGeneration(id);
  const [refreshingVideos, setRefreshingVideos] = useState(false);
  const [loadingMoreVideos, setLoadingMoreVideos] = useState(false);

  const latestAudio = assets.audio?.[0] || null;
  const latestImage = assets.images?.[0] || null;
  const latestVideo = assets.videos?.[0] || null;
  const projectVideos = Array.isArray(assets.videos) ? assets.videos : [];
  const nonVideoAssets = useMemo(() => ({ ...assets, videos: [] }), [assets]);
  const videoPagination = pagination?.videos || {
    total: projectVideos.length,
    offset: 0,
    limit: VIDEO_PAGE_SIZE
  };
  const canLoadMoreVideos = videoPagination.offset + videoPagination.limit < videoPagination.total;
  const hasAudio = Boolean(latestAudio);
  const hasImage = Boolean(latestImage);
  const hasVideo = Boolean(latestVideo);

  // Determine workflow step
  const workflowStep = useMemo(() => {
    if (!hasAudio && !hasImage) return 1;
    if (hasAudio && !hasImage) return 2;
    if (hasAudio && hasImage && !hasVideo) return 3;
    if (hasVideo) return 4;
    return 1;
  }, [hasAudio, hasImage, hasVideo]);

  // Load project data
  const loadProject = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [projectData, projectList] = await Promise.all([
        projectService.getProject(id),
        projectService.listProjects()
      ]);
      
      setProject({
        ...projectData,
        musicPath: resolveMediaUrl(projectData?.musicPath || projectData?.music_path || '')
      });
      
      setProjects(projectList);
      await loadAssets();

    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [id, loadAssets, hasAudio, hasImage, hasVideo]);

  const handleRefreshVideos = useCallback(async () => {
    setRefreshingVideos(true);
    try {
      await loadAssets({ suppressLoading: true });
    } finally {
      setRefreshingVideos(false);
    }
  }, [loadAssets]);

  const handleLoadMoreVideos = useCallback(async () => {
    if (!canLoadMoreVideos || loadingMoreVideos) return;
    const nextOffset = (videoPagination.offset || 0) + (videoPagination.limit || VIDEO_PAGE_SIZE);
    setLoadingMoreVideos(true);
    try {
      await loadAssets({ videosOffset: nextOffset, appendVideos: true, suppressLoading: true });
    } finally {
      setLoadingMoreVideos(false);
    }
  }, [canLoadMoreVideos, loadingMoreVideos, videoPagination.offset, videoPagination.limit, loadAssets]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  // Toast notifications
  const showToast = useCallback((type, message) => {
    try {
      toastBus({ type, message });
    } catch {}
  }, []);

  useEffect(() => {
    const completed = [];
    if (hasAudio) completed.push(2);
    if (hasImage) completed.push(3);
    if (hasVideo) completed.push(4);
    setCompletedSteps(completed);
  }, [hasAudio, hasImage, hasVideo]);

  useEffect(() => {
    if (!id) return;
    try {
      const key = `composer_video_prediction_${String(id)}`;
      const existing = window.sessionStorage.getItem(key);
      if (existing && !videoGen.predictionId) {
        videoGen.startPolling(existing);
      }
    } catch {}
  }, [id, videoGen.predictionId, videoGen.startPolling]);

  useEffect(() => {
    if (!id) return;
    try {
      const key = `composer_video_prediction_${String(id)}`;
      if (videoGen.predictionId) window.sessionStorage.setItem(key, String(videoGen.predictionId));
      if (!videoGen.predictionId) window.sessionStorage.removeItem(key);
    } catch {}
  }, [id, videoGen.predictionId]);

  useEffect(() => {
    if (!videoGen?.telemetry) return;
    if (videoGen.telemetry?.finalStatus === 'completed' && !videoHandledRef.current.completed) {
      videoHandledRef.current.completed = true;
      showToast('success', 'Video generated');
      loadProject();
      return;
    }
  }, [videoGen.telemetry, loadProject, showToast]);

  useEffect(() => {
    if (!videoGen?.error) return;
    if (!videoHandledRef.current.failed) {
      videoHandledRef.current.failed = true;
      showToast('error', videoGen.error);
    }
  }, [videoGen.error, showToast]);

  // Handle asset generation
  const handleGenerateAsset = useCallback(async (payload) => {
    const generationId = `${payload.type}-${Date.now()}`;
    
    setActiveGenerations(prev => [...prev, {
      id: generationId,
      type: payload.type,
      status: 'pending',
      progress: 0
    }]);

    try {
      setBusy(true);
      let result;

      switch (payload.type) {
        case 'audio':
          result = await api.composerCreateAudio(id, {
            specificContext: payload.specificContext,
            chosenPrompt: payload.chosenPrompt,
            tier: payload.tier
          });
          if (result?.prompts) setAudioPrompts(cleanPrompts(result.prompts));
          break;
          
        case 'image':
          result = await api.composerCreateImage(id, {
            specificContext: payload.specificContext,
            chosenPrompt: payload.chosenPrompt,
            tier: payload.tier
          });
          if (result?.prompts) setImagePrompts(cleanPrompts(result.prompts));
          break;
          
        case 'video':
          const latestAudio = assets.audio[0];
          const latestImage = assets.images[0];
          
          if (!latestAudio || !latestImage) {
            showToast('error', 'Need both audio and image for video generation');
            return;
          }

          videoHandledRef.current = { completed: false, failed: false };
          try {
            await saveContext();
          } catch {}

          const predictionId = await videoGen.startVideoGeneration({
            ...payload,
            audioAssetId: latestAudio.id || latestAudio.assetId || latestAudio._id,
            imageAssetId: latestImage.id || latestImage.assetId || latestImage._id
          });

          showToast('info', 'Video generation started');
          videoGen.startPolling(predictionId);
          result = { ok: true, predictionId };
          break;
          
        default:
          throw new Error('Invalid asset type');
      }

      if (result?.ok) {
        if (payload.type === 'video') {
          showToast('info', 'Video generation is running…');
        } else {
          showToast('success', `${payload.type.charAt(0).toUpperCase() + payload.type.slice(1)} generated successfully`);
          await loadProject();
        }
      }
      
    } catch (error) {
      showToast('error', `Failed to generate ${payload.type}: ${error.message}`);
    } finally {
      setActiveGenerations(prev => prev.filter(g => g.id !== generationId));
      setBusy(false);
    }
  }, [id, assets, videoGen, loadProject, showToast]);

  // Handle quick actions
  const handleQuickAction = useCallback(async (action) => {
    switch (action.type) {
      case 'audio':
        await handleGenerateAsset({ type: 'audio', tier: 'loudest' });
        break;
      case 'image':
        await handleGenerateAsset({ type: 'image', tier: 'loudest' });
        break;
      case 'video':
        if (hasAudio && hasImage) {
          await handleGenerateAsset({ type: 'video', tier: 'loudest', template: 'animated' });
        }
        break;
      case 'batch-audio':
        // Handle batch generation
        showToast('info', 'Batch audio generation coming soon');
        break;
      case 'optimize':
        showToast('info', 'Asset optimization coming soon');
        break;
    }
  }, [handleGenerateAsset, hasAudio, hasImage, showToast]);

  // Handle workflow step navigation
  const handleWorkflowStep = useCallback((step) => {
    switch (step) {
      case 1:
        // Focus on context setup
        document.querySelector('[data-section="context"]')?.scrollIntoView({ behavior: 'smooth' });
        break;
      case 2:
        // Focus on audio generation
        document.querySelector('[data-section="audio"]')?.scrollIntoView({ behavior: 'smooth' });
        break;
      case 3:
        // Focus on image generation
        document.querySelector('[data-section="image"]')?.scrollIntoView({ behavior: 'smooth' });
        break;
      case 4:
        // Focus on video generation
        document.querySelector('[data-section="video"]')?.scrollIntoView({ behavior: 'smooth' });
        break;
    }
  }, []);

  const handleGenerateSelected = useCallback(async () => {
    if (!selectedTypes.audio && !selectedTypes.image && !selectedTypes.video) {
      showToast('info', 'Select at least one asset type to generate');
      return;
    }

    // Sequentially generate selected asset types using existing handler
    if (selectedTypes.audio) {
      await handleGenerateAsset({
        type: 'audio',
        tier: 'loudest',
        specificContext: audioSpecificContext,
        chosenPrompt: audioSelectedPrompt
      });
    }
    if (selectedTypes.image) {
      await handleGenerateAsset({
        type: 'image',
        tier: 'loudest',
        specificContext: imageSpecificContext,
        chosenPrompt: imageSelectedPrompt
      });
    }
    if (selectedTypes.video) {
      await handleGenerateAsset({
        type: 'video',
        tier: 'loudest',
        template: 'animated',
        specificContext: videoSpecificContext,
        chosenPrompt: videoSelectedPrompt
      });
    }
  }, [
    selectedTypes,
    handleGenerateAsset,
    showToast,
    audioSpecificContext,
    audioSelectedPrompt,
    imageSpecificContext,
    imageSelectedPrompt,
    videoSpecificContext,
    videoSelectedPrompt
  ]);

  if (loading) return <div style={{ padding: 16, color: '#e5e7eb' }}>Loading...</div>;
  if (error) return <div style={{ padding: 16, color: '#ef4444' }}>Error: {error}</div>;

  return (
    <VideoGenerationErrorBoundary>
      <div style={{ padding: 16, maxWidth: 1200, margin: '0 auto' }}>
        {/* Header with Project Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <Link to="/projects" style={{ color: '#ff7a45', textDecoration: 'none', fontSize: 14 }}>← Back</Link>
          
          <ProjectSelector
            projects={projects}
            currentProjectId={id}
            onSelectProject={(projectId) => {
              // Navigation handled by router
            }}
          />
          
          <button onClick={() => window.location.reload()} style={{ marginLeft: 'auto', background: '#374151', color: '#fff', border: 0, borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>
            Refresh
          </button>
        </div>

        {/* Latest asset shortcuts */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          {latestAudio ? (
            <LatestAssetChip
              label="Latest Voice"
              hint={latestAudio?.url ? decodeURIComponent(latestAudio.url.split('/').pop() || '') : ''}
              thumbUrl={null}
              onClick={() => document.querySelector('[data-section="audio"]')?.scrollIntoView({ behavior: 'smooth' })}
            />
          ) : null}
          {latestImage ? (
            <LatestAssetChip
              label="Latest Image"
              hint={latestImage?.url ? decodeURIComponent(latestImage.url.split('/').pop() || '') : ''}
              thumbUrl={latestImage.thumbnailUrl || latestImage.url || null}
              onClick={() => document.querySelector('[data-section="image"]')?.scrollIntoView({ behavior: 'smooth' })}
            />
          ) : null}
          {latestVideo ? (
            <LatestAssetChip
              label="Latest Video"
              hint={latestVideo?.url ? decodeURIComponent(latestVideo.url.split('/').pop() || '') : ''}
              thumbUrl={latestVideo.thumbnailUrl || latestVideo.url || null}
              onClick={() => document.querySelector('[data-section="video"]')?.scrollIntoView({ behavior: 'smooth' })}
            />
          ) : null}
        </div>

        <div style={{ marginTop: 20 }}>
          <VideoGallery
            videos={projectVideos}
            onRefresh={handleRefreshVideos}
            refreshing={refreshingVideos}
            canLoadMore={canLoadMoreVideos}
            onLoadMore={canLoadMoreVideos ? handleLoadMoreVideos : undefined}
            loadingMore={loadingMoreVideos}
          />
        </div>

        <div style={{ marginTop: 20 }}>
          <AssetGallery
            assets={nonVideoAssets}
            archived={archived}
            loading={loading}
            onArchive={archiveAsset}
            onRestore={restoreAsset}
            onDeleteArchived={deleteArchivedAsset}
            onRefresh={loadAssets}
          />
        </div>

        {/* Workflow Progress */}
        <div style={{ marginBottom: 20 }}>
          <WorkflowWizard
            currentStep={workflowStep}
            completedSteps={completedSteps}
            onStepClick={handleWorkflowStep}
          />
        </div>

        {/* Active Generations */}
        {(activeGenerations.length > 0 || videoGen.isPolling || videoGen.predictionId) && (
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ color: '#f9fafb', margin: '0 0 12px 0', fontSize: 16 }}>
              Active Generations
            </h3>
            {(videoGen.isPolling || videoGen.predictionId) && (
              <UnifiedProgress
                type="video"
                status={videoGen.predictionStatus || (videoGen.isPolling ? 'running' : 'pending')}
                progress={Number.isFinite(Number(videoGen.progress)) ? Number(videoGen.progress) : 0}
                onCancel={async () => {
                  try {
                    if (!videoGen.predictionId) return;
                    const res = await api.composerVideoCancel(id, videoGen.predictionId);
                    if (res?.error) throw new Error(res.error);
                    showToast('success', 'Video generation cancelled');
                    videoGen.reset();
                  } catch (e) {
                    showToast('error', e?.message || 'Cancel failed');
                  }
                }}
              />
            )}
            {activeGenerations.map(gen => (
              <UnifiedProgress
                key={gen.id}
                type={gen.type}
                status={gen.status}
                progress={gen.progress}
                onCancel={() => {
                  // Handle cancellation
                }}
              />
            ))}
          </div>
        )}

        <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))' }}>
          {/* Left Column */}
          <div style={{ display: 'grid', gap: 20 }}>
            <div data-section="context">
              <ProjectBasics
                project={project}
                contextText={context.text}
                setContextText={(text) => updateContext({ text })}
                contextFilesText={context.files.join('\n')}
                setContextFilesText={(files) => updateContext({ files: files.split('\n').filter(f => f.trim()) })}
                contextTags={context.tags}
                setContextTags={(tags) => updateContext({ tags })}
                contextUploads={context.uploads}
                setContextUploads={(uploads) => updateContext({ uploads })}
                onSaveContext={() => saveContext()}
                busy={busy}
              />
            </div>
            
            <QuickActions
              assets={assets}
              onQuickAction={handleQuickAction}
              busy={busy}
            />
          </div>

          {/* Right Column */}
          <div style={{ display: 'grid', gap: 20 }}>
            {/* Generate Selected Assets */}
            <div>
              <div style={{ background: '#111827', border: '1px solid #1f2937', borderRadius: 10, padding: 12 }}>
                <div style={{ color: '#f9fafb', fontWeight: 600, marginBottom: 8 }}>Generate Selected Assets</div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#e5e7eb', fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={selectedTypes.audio}
                      onChange={(e) => setSelectedTypes(prev => ({ ...prev, audio: e.target.checked }))}
                    />
                    Voice
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#e5e7eb', fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={selectedTypes.image}
                      onChange={(e) => setSelectedTypes(prev => ({ ...prev, image: e.target.checked }))}
                    />
                    Images
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#e5e7eb', fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={selectedTypes.video}
                      onChange={(e) => setSelectedTypes(prev => ({ ...prev, video: e.target.checked }))}
                    />
                    Video
                  </label>
                </div>
                <button
                  onClick={handleGenerateSelected}
                  disabled={busy}
                  style={{
                    background: '#ff5a1f',
                    color: '#fff',
                    border: 0,
                    borderRadius: 8,
                    padding: '8px 12px',
                    cursor: busy ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 600
                  }}
                >
                  Generate Selected
                </button>
              </div>
            </div>

            <div data-section="creation">
              <AssetCreationFlow
                projectId={id}
                availableAssets={assets}
                onGenerateAsset={handleGenerateAsset}
                busy={busy}
              />
            </div>
            
            <div style={{ background: '#111827', border: '1px solid #1f2937', borderRadius: 10, padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ color: '#f9fafb', fontWeight: 600 }}>Prompts (advanced)</div>
                <button
                  onClick={() => setShowPrompts((v) => !v)}
                  style={{
                    background: '#1f2937',
                    color: '#e5e7eb',
                    border: '1px solid #374151',
                    borderRadius: 8,
                    padding: '6px 10px',
                    fontSize: 12,
                    cursor: 'pointer'
                  }}
                >
                  {showPrompts ? 'Hide' : 'Show'}
                </button>
              </div>
              {showPrompts ? (
                <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
                  <div data-section="audio">
                    <PromptSection
                      type="audio"
                      projectId={id}
                      project={project}
                      specificContext={audioSpecificContext}
                      setSpecificContext={setAudioSpecificContext}
                      selectedPrompt={audioSelectedPrompt}
                      setSelectedPrompt={setAudioSelectedPrompt}
                      prompts={audioPrompts}
                      setPrompts={setAudioPrompts}
                      busy={busy}
                    />
                  </div>
                  
                  <div data-section="image">
                    <PromptSection
                      type="image"
                      projectId={id}
                      project={project}
                      specificContext={imageSpecificContext}
                      setSpecificContext={setImageSpecificContext}
                      selectedPrompt={imageSelectedPrompt}
                      setSelectedPrompt={setImageSelectedPrompt}
                      prompts={imagePrompts}
                      setPrompts={setImagePrompts}
                      busy={busy}
                    />
                  </div>

                  <div data-section="video">
                    <PromptSection
                      type="video"
                      projectId={id}
                      project={project}
                      specificContext={videoSpecificContext}
                      setSpecificContext={setVideoSpecificContext}
                      selectedPrompt={videoSelectedPrompt}
                      setSelectedPrompt={setVideoSelectedPrompt}
                      prompts={videoPrompts}
                      setPrompts={setVideoPrompts}
                      busy={busy}
                    />
                  </div>
                </div>
              ) : (
                <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 8 }}>
                  Optional: expand to fine-tune prompts for each asset type.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Audio Player */}
        <audio ref={audioPlayerRef} style={{ display: 'none' }} />

        {/* Modals */}
        <StaticVideoModal
          open={staticModalOpen}
          onClose={() => setStaticModalOpen(false)}
          projectId={id}
          assets={assets}
        />
      </div>
    </VideoGenerationErrorBoundary>
  );
}
